// react
import { ChangeEvent, Dispatch, MouseEvent, SetStateAction, useContext, useState } from 'react';

// types
import {
	Contract,
	ContractAmount,
	ContractPayload,
	Entity,
	ErrorCause,
	Feedback,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner, ContextUserEntity } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Grid, SelectChangeEvent } from '@mui/material';

// fields
import { AmountsMultiCurrency } from '../../fields/AmountsMultiCurrency/AmountsMultiCurrency';
import { FilePicker } from '../../fields/FilePicker/FilePicker';
import { Input } from '../../fields/Input/Input';
import { Select } from '../../fields/Select/Select';

// ui
import { AlertFeedback } from '../../ui/AlertFeedback/AlertFeedback';

// utils
import { formatDate } from '../../../utils/dates';

// props
export interface FormManageContractProps {
	contract?: Contract;
	entity?: Entity;
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormManageContract = ({
	contract,
	entity,
	onClose,
	onSuccess,
	setIsLoading,
}: FormManageContractProps) => {
	// hooks
	const { t } = useTranslation();

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	const { userEntity } = useContext(ContextUserEntity);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// defaults
	let defaultEntities: number[] = [];
	let defaultAmounts: ContractPayload['amounts'] = [];

	if (contract?.entities) {
		defaultEntities = (contract.entities as Entity[]).map((entity) => entity.id);
	}

	if (contract?.amounts) {
		defaultAmounts = contract.amounts;
	} else {
		// Default to one empty amount for new contracts
		defaultAmounts = [
			{
				amount: 0,
				currency: '',
			},
		];
	}

	if (entity) {
		defaultEntities = [entity.id];
	}

	// state
	const [entities, setEntities] = useState<Entity[]>([]);
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [files, setFiles] = useState<File[]>([]);
	const [values, setValues] = useState({
		amounts: defaultAmounts,
		comment: '',
		dateSigning: contract?.dateSigning
			? formatDate({ date: new Date(contract.dateSigning), format: 'YYYY-MM-DD' })
			: '',
		description: contract?.description || '',
		entities: defaultEntities,
		idStep: contract?.idStep || '',
		num: contract?.num || '',
	});

	// vars
	const idForm = 'form-manage-contract';
	const isEdit = Boolean(contract);

	// fetch entities
	const urlEntities = `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&fields=id,name&type=SUPPLIER`;

	useFetch({
		url: urlEntities,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setEntities(res.data);
			}
		},
	});

	// options
	const optionsEntities = entities.map((entity) => {
		return {
			label: entity.name,
			value: entity.id,
		};
	});

	// handlers
	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	const handleAmountsChange = (amounts: ContractAmount[] | ContractPayload['amounts']) => {
		setValues({
			...values,
			amounts: amounts as ContractAmount[],
		});
	};

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		const entitiesNew = [...values.entities];

		if (!contract && userEntity?.id) {
			entitiesNew.push(userEntity.id);
		}

		try {
			// clear error
			setFeedback(null);

			// check for duplicate currencies
			const currenciesSelected = values.amounts?.map((amount) => amount.currency) || [];
			const currenciesUnique = new Set(currenciesSelected);
			if (currenciesSelected.length !== currenciesUnique.size) {
				setFeedback({
					alert: {
						children: t('error.duplicateCurrency'),
					},
				});
				return;
			}

			// set loader
			setIsLoading(true);

			let commentId = null;
			let changedAmounts: ContractPayload['amounts'] = [];

			// If editing contract, check for amount changes
			if (isEdit) {
				// Compare current amounts with original amounts to find changes
				const originalAmounts = contract?.amounts || [];
				const currentCurrencies = values.amounts.map((amount) => amount.currency);
				const originalCurrencies = originalAmounts.map((amount) => amount.currency);

				// Find amounts that have changed (different values) or are new
				const modifiedAmounts = values.amounts.filter((currentAmount) => {
					const originalAmount = originalAmounts.find(
						(orig) => orig.currency === currentAmount.currency
					);

					// Include if it's a new currency or the amount has changed
					return !originalAmount || originalAmount.amount !== currentAmount.amount;
				});

				// Find currencies that were removed (set them to zero)
				const removedCurrencies = originalCurrencies.filter(
					(currency) => !currentCurrencies.includes(currency)
				);

				// Only include amounts that have changed or been removed
				const hasChanges = modifiedAmounts.length > 0 || removedCurrencies.length > 0;

				if (hasChanges) {
					changedAmounts = [
						// Include only modified/new amounts
						...modifiedAmounts.map((amount) => ({
							amount: amount.amount,
							currency: amount.currency,
						})),
						// Include removed currencies with zero amounts
						...removedCurrencies.map((currency) => ({
							amount: 0,
							currency: currency,
						})),
					];
				}

				if (values.comment) {
					const commentBody = {
						comment: values.comment,
						project: project?.id,
					};

					const fetchComment = await fetch(`${process.env.REACT_APP_API_URL}/comments`, {
						method: 'POST',
						headers: {
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
							'Content-Type': 'application/json',
						},
						body: JSON.stringify(commentBody),
					});
					const resComment = await fetchComment.json();

					if (resComment.error) {
						throw new Error(resComment.error.message, {
							cause: {
								id: resComment.error.id,
								res: resComment,
							},
						});
					}

					commentId = resComment.data.id;
				}
			} else {
				// For new contracts, include all amounts
				changedAmounts = values.amounts.map((amount) => ({
					amount: amount.amount,
					currency: amount.currency,
				}));
			}

			const body: ContractPayload = {
				dateSigning: values.dateSigning
					? formatDate({
							date: new Date(values.dateSigning),
							format: 'YYYY-MM-DD',
					  })
					: undefined,
				description: values.description,
				entities: entitiesNew,
				idStep: values.idStep,
				num: values.num,
			};

			// Only include project ID if creating new contract
			if (!isEdit) {
				body.project = project?.id;
			}

			// Only include amounts if there are changes (for edits) or if creating new contract
			if (changedAmounts.length > 0) {
				body.amounts = changedAmounts;
			}

			// Add comment reference for amendments
			if (commentId) {
				body.comment = commentId;
			}

			let url = `${process.env.REACT_APP_API_URL}/contracts`;
			if (contract) {
				url += `/${contract.id}`;
			}

			const fetchAddContract = await fetch(url, {
				method: isEdit ? 'PATCH' : 'POST',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify(body),
			});
			const resAddContract = await fetchAddContract.json();

			if (resAddContract.error) {
				throw new Error(resAddContract.error.message, {
					cause: {
						id: resAddContract.error.id,
						res: resAddContract,
					},
				});
			}

			const contractNew = resAddContract.data;

			// add documents using files
			for (let i = 0; i < files.length; i++) {
				// define form data
				const formData = new FormData();
				const dateToday = new Date();
				const year = dateToday.getFullYear();
				const month = new Intl.DateTimeFormat('en-US', {
					month: '2-digit',
				}).format(dateToday);

				// append form data
				formData.append(
					'path',
					`bldt/projects/${project?.id}/contracts/${contractNew.id}/${year}/${month}/`
				);
				formData.append('file', files[i]);
				formData.append('contract', String(contractNew.id));

				if (project) {
					formData.append('project', String(project.id));
				}

				// POST document
				const fetchPostDoc = await fetch(`${process.env.REACT_APP_API_URL}/documents`, {
					method: 'post',
					body: formData,
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				});

				const resPostDoc = await fetchPostDoc.json();

				if (resPostDoc.error) {
					throw new Error(resPostDoc.error.message, {
						cause: {
							id: resPostDoc.error.id,
							res: resPostDoc,
						},
					});
				}
			}

			if (onSuccess) {
				onSuccess();
			}

			// banner message
			let bannerMessage = t('alert.contractCreated', { num: entries.num });
			if (isEdit) {
				bannerMessage = t('alert.contractUpdated', { num: entries.num });
			}
			// set success banner
			setBanner({
				hasClose: true,
				message: bannerMessage,
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew: Feedback = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				if (cause.id === 'CONTRACT_DUPLICATE') {
					feedbackNew.alert.children = t('error.contractDuplicate', { num: entries.num });
				}

				if (cause.id === 'CONTRACT_ENTITIES_NON_EXISTENT') {
					feedbackNew.alert.children = t('error.contractSupplierEmpty');
				}

				if (cause.id === 'CONTRACT_ID_NON_EXISTENT') {
					feedbackNew.alert.children = t('error.contractIdNonExistent');
				}

				if (cause.id === 'CONTRACT_NON_EXISTENT') {
					feedbackNew.alert.children = t('error.contractNonExistent', { id: contract?.id });
				}

				if (cause.id === 'CONTRACT_UNAUTHORIZED_POST') {
					feedbackNew.alert.children = t('error.unauthorized');
				}

				if (cause.id === 'CONTRACT_COMMENT_REQUIRED') {
					feedbackNew.alert.children = t('error.contractCommentRequired');
				}

				if (cause.id === 'DOCUMENT_CONTRACT_NON_EXISTENT') {
					feedbackNew.alert.children = t('error.documentContractNonExistent');
				}

				// store full error response for debugging
				if (cause.res) {
					feedbackNew.response = cause.res;
				}
			}

			// set error
			setFeedback(feedbackNew);
		} finally {
			// set loader
			setIsLoading(false);
		}
	};

	return (
		<form id={`${idForm}`} name="formManageContract" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-num`}
						label={t('referenceNumber')}
						name="num"
						onChange={handleChangeValue}
						required={true}
						value={values.num}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Select
						disabled={entities.length === 0}
						id={`${idForm}-entities`}
						label={t('suppliers')}
						multiple={true}
						name="entities"
						onChange={handleChangeValue}
						options={optionsEntities}
						required={true}
						value={values.entities}
					/>
				</Grid>
				<Grid item xs={12}>
					<AmountsMultiCurrency
						amounts={values.amounts}
						onChange={handleAmountsChange}
						idForm={idForm}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-step-id`}
						label={t('stepId')}
						name="idStep"
						onChange={handleChangeValue}
						required={false}
						value={values.idStep}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-date-signing`}
						inputProps={{
							max: new Date().toISOString().split('T')[0],
						}}
						label={t('signingDate')}
						name="dateSigning"
						onChange={handleChangeValue}
						required={false}
						type="date"
						value={values.dateSigning}
					/>
				</Grid>
				{!contract && (
					<Grid item xs={12}>
						<FilePicker
							onChange={(e, filesSelected) => {
								setFiles(filesSelected);
							}}
						/>
					</Grid>
				)}
				<Grid item xs={12}>
					<Input
						id={`${idForm}-description`}
						label={t('description')}
						minRows={2}
						multiline={true}
						name="description"
						onChange={handleChangeValue}
						value={values.description}
					/>
				</Grid>
				{isEdit && (
					<Grid item xs={12}>
						<Input
							id={`${idForm}-comment`}
							label={t('comment')}
							name="comment"
							onChange={handleChangeValue}
							required={true}
							multiline={true}
							minRows={2}
							value={values.comment}
						/>
					</Grid>
				)}

				{feedback && (
					<Grid item xs={12}>
						<AlertFeedback feedback={feedback} />
					</Grid>
				)}
			</Grid>
		</form>
	);
};
